import axios, { AxiosInstance } from "axios";
import { formFields } from "./form_fields";
import { ApiResponse, Meta } from "../types/types";
import { formMappings } from "../config/formMappings";

// Basic Auth credentials
const username = "GJRE-7YLX-YF4L-6UQZ";
const password = "x";

// Axios instance for the API
const api: AxiosInstance = axios.create({
  baseURL: "https://conference.msapsg.org/wp-json/frm/v2",
  headers: {
    "Content-Type": "application/json",
    Authorization: "Basic " + btoa(`${username}:${password}`),
  },
});

/**
 * Function to fetch and map form entries.
 *
 * @param formId - The ID of the form to fetch entries for.
 * @returns A promise that resolves with all paid entries.
 */
export const getFormEntries = async (formId: string): Promise<any> => {
  try {
    const perPage = 500;

    const response = await api.get<ApiResponse>(`/forms/${formId}/entries`, {
      params: {
        page_size: perPage,
      },
    });

    const data = response.data;

    // Map field keys to field names in each entry
    const mappedData = Object.keys(data).map((entryKey) => {
      const entry = data[entryKey];

      // Transform meta fields
      const mappedMeta = Object.keys(entry.meta).reduce((acc, key) => {
        // Look up human-readable name or fall back to the original key
        const fieldName = formFields[key]?.name || key;
        acc[fieldName] = entry.meta[key];
        return acc;
      }, {} as Meta);

      return {
        ...entry,
        meta: mappedMeta,
      };
    });

    // Filter for only entries where "Paid?" is "Yes"
    // const paidEntries = mappedData.filter(
    //   (entry) => entry.meta["Paid?"] === "Yes"
    // );

    return mappedData;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("API Error:", error.message);
    } else {
      console.error("Unexpected Error:", error);
    }
    throw error;
  }
};

/**
 * Sends a PATCH request to check in multiple registrants.
 *
 * @param ids - Array of entry IDs to update.
 * @param parentEntryId - The parent entry ID to include in the request body.
 * @param activeFormName - The active form name to determine which form mapping to use.
 * @returns A promise that resolves when all requests are complete.
 */
export async function checkInRegistrants(
  ids: string[],
  parentEntryId: string,
  activeFormName: string
): Promise<void> {
  // Get form mapping from formMappings based on activeFormName
  const formMapping = formMappings[activeFormName as keyof typeof formMappings];

  if (!formMapping) {
    throw new Error(`Form mapping not found for form name: ${activeFormName}`);
  }

  // Get checkedInID from formMappings for updating (not for reading)
  const checkedInID = (formMapping.fieldMappings as any)?.checkedInID;

  if (!checkedInID) {
    throw new Error("checkedInID not found in formMappings");
  }

  // Note: form_id 14 appears to be the registrant sub-form, which is separate from the main form
  // This might need to be made configurable if different forms use different registrant form IDs
  const requests = ids.map((id) => {
    const payload: any = {
      [checkedInID]: "true",
      parent_item_id: parentEntryId,
      form_id: formMapping.registrantFormId,
    };

    return api.patch(`/entries/${id}`, payload);
  });

  try {
    await Promise.all(requests);
  } catch (error) {
    console.error("Error checking in registrants:", error);
    throw error;
  }
}

/**
 * Sends a PATCH request to mark paid field for a registrant.
 *
 * @param id - Entry ID to update.
 * @param paidStatus - Boolean indicating if the registrant is paid (true or false).
 * @param activeFormName - The active form name to determine which form mapping to use.
 * @returns A promise that resolves when the request is complete.
 */
export async function markPaid(
  id: string,
  paidStatus: boolean,
  activeFormName: string
): Promise<void> {
  // Get form mapping from formMappings based on activeFormName
  const formMapping = formMappings[activeFormName as keyof typeof formMappings];

  if (!formMapping) {
    throw new Error(`Form mapping not found for form name: ${activeFormName}`);
  }

  // Get paidID from formMappings for updating (not for reading)
  const paidID = (formMapping.fieldMappings as any)?.paidID;

  if (!paidID) {
    throw new Error("paidID not found in formMappings");
  }

  // Create payload with paidID as key
  const payload: any = {
    [paidID]: paidStatus ? "Yes" : "No",
    form_id: parseInt(formMapping.formId, 10),
  };

  try {
    await api.patch(`/entries/${id}`, payload);
  } catch (error) {
    console.error(
      `Error marking ${paidStatus ? "paid" : "unpaid"} for registrant:`,
      error
    );
    throw error;
  }
}

/**
 * Sends a PATCH request to update notes field for an entry.
 *
 * @param id - Entry ID to update.
 * @param notes - The notes text to save.
 * @param activeFormName - The active form name to determine which form mapping to use.
 * @returns A promise that resolves when the request is complete.
 */
export async function updateNotes(
  id: string,
  notes: string,
  activeFormName: string
): Promise<void> {
  // Get form mapping from formMappings based on activeFormName
  const formMapping = formMappings[activeFormName as keyof typeof formMappings];

  if (!formMapping) {
    throw new Error(`Form mapping not found for form name: ${activeFormName}`);
  }

  // Get notesID from formMappings for updating (not for reading)
  const notesID = (formMapping.fieldMappings as any)?.notesID;

  if (!notesID) {
    throw new Error("notesID not found in formMappings");
  }

  // Create payload with notesID as key
  const payload: any = {
    [notesID]: notes,
    form_id: parseInt(formMapping.formId, 10),
  };

  try {
    await api.patch(`/entries/${id}`, payload);
  } catch (error) {
    console.error("Error updating notes for entry:", error);
    throw error;
  }
}
